import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Activity,
  Bell,
  Building2,
  Compass,
  FileBarChart2,
  HelpCircle,
  Layers,
  LogOut,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/api/client";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { CreateWorkspaceModal } from "./CreateWorkspaceModal";
import { useWorkspacesStore } from "@/stores/workspaces";
import { useTopicsStore } from "@/stores/topics";
import { deltaDirection, sentimentBand } from "@/components/topics/shared";

// Keep in sync with the View union + routes in App.tsx. Only these
// destinations appear in the nav — everything else is reachable from them
// (Help lives in the footer).
type View = "health" | "reports" | "explore" | "brands" | "alerts" | "help";

interface SidebarProps {
  onWorkspaceSwitch: () => void;
  onLogout: () => void;
}

const primaryNav: { id: View; to: string; label: string; icon: typeof Activity }[] = [
  { id: "health", to: "/app/health", label: "Brand Health", icon: Activity },
  { id: "reports", to: "/app/reports", label: "Reports", icon: FileBarChart2 },
  { id: "explore", to: "/app/explore", label: "Explore", icon: Compass },
];

// Topics now own prompts, competitors, and tracking, so Configure shrinks to the
// two nouns that live outside a topic space: who you track and what watches it.
const configureNav: { id: View; to: string; label: string; icon: typeof Activity }[] = [
  { id: "brands", to: "/app/brands", label: "Brands", icon: Building2 },
  { id: "alerts", to: "/app/alerts", label: "Alerts", icon: Bell },
];

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium transition-colors",
    isActive
      ? "bg-white/10 text-white"
      : "text-brand-400 hover:text-white hover:bg-white/5"
  );

export function Sidebar({ onWorkspaceSwitch, onLogout }: SidebarProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [unackedAlerts, setUnackedAlerts] = useState(0);
  const switchTo = useWorkspacesStore(s => s.switchTo);
  const currentWorkspaceId = useWorkspacesStore(s => s.currentId);
  const topics = useTopicsStore(s => s.topics);
  const fetchTopics = useTopicsStore(s => s.fetch);

  // Unread-alerts badge: refresh on workspace change and once a minute.
  // Decorative — failures are silently ignored.
  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      api.alerts.unackedCount()
        .then(({ count }) => { if (!cancelled) setUnackedAlerts(count); })
        .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [currentWorkspaceId]);

  // Topics nav list — reload when the workspace switches (the sidebar persists
  // across switches, so it drives the topics store's refetch).
  useEffect(() => { fetchTopics(); }, [currentWorkspaceId, fetchTopics]);

  // Only arena topics get a nav entry; the pulse topic lives under Brand Health.
  // Ghost topics — 0 active prompts AND tracking disabled (e.g. a drifted
  // 'general') — are hidden from the nav only; they stay reachable by URL and
  // in the topics payload.
  const arenaTopics = topics.filter(t => t.kind === "arena" && (t.prompt_count > 0 || t.tracking_enabled));

  return (
    <aside className="w-[240px] shrink-0 bg-brand-950 flex flex-col h-screen">
      {/* Brand */}
      <div className="px-6 pt-7 pb-4">
        <Link to="/app/health" className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-brand-600 flex items-center justify-center">
            <Layers className="w-4 h-4 text-white" />
          </div>
          <span className="text-[17px] font-bold text-white tracking-tight">
            Prism
          </span>
        </Link>
      </div>

      {/* Workspace Switcher */}
      <WorkspaceSwitcher
        onSwitch={onWorkspaceSwitch}
        onCreateNew={() => setCreateOpen(true)}
      />

      <CreateWorkspaceModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(newId) => {
          switchTo(newId);
          onWorkspaceSwitch();
        }}
      />

      {/* Primary Navigation */}
      <nav className="flex-1 px-3 mt-6">
        <div className="space-y-0.5">
          {primaryNav.map((item) => (
            <NavLink key={item.id} to={item.to} className={navLinkClass}>
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
              {/* Unacknowledged alerts surface on Brand Health — that's where "what changed" lives. */}
              {item.id === "health" && unackedAlerts > 0 && (
                <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-accent-500 text-brand-950 text-[10px] font-bold flex items-center justify-center">
                  {unackedAlerts > 99 ? "99+" : unackedAlerts}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        {/* Topics Section — one entry per arena topic (pulse lives on Brand Health).
            Always rendered so "+ New topic" stays reachable even with no topics yet. */}
        <div className="mt-8">
          <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.08em] uppercase text-brand-700">
            Topics
          </p>
          <div className="space-y-0.5">
            {arenaTopics.map((t) => {
              // The dot stays the tracking on/off signal (accepted deviation); when
              // tracking is on we band its color by the topic's field sentiment.
              const band = sentimentBand(t.field_sentiment);
              const dotClass = !t.tracking_enabled
                ? "bg-brand-700"
                : band === "good" ? "bg-green-500"
                : band === "mid" ? "bg-accent-400"
                : band === "low" ? "bg-red-400"
                : "bg-brand-600";
              const valClass = band === "good" ? "text-green-400"
                : band === "mid" ? "text-accent-400"
                : band === "low" ? "text-red-400"
                : "text-brand-400";
              // Tooltip carries the field sentiment + WoW delta (the value pill
              // has no room for the movement glyph).
              const label = t.name.charAt(0).toUpperCase() + t.name.slice(1);
              const dir = deltaDirection(t.field_sentiment_delta);
              const title = t.field_sentiment === null
                ? label
                : `${label} · ${Math.round(t.field_sentiment)} field sentiment`
                  + (dir === "none" ? ""
                    : ` · ${dir === "up" ? "▲ +" : dir === "down" ? "▼ " : ""}${t.field_sentiment_delta!.toFixed(1)} WoW`);
              return (
                <NavLink key={t.id} to={`/app/topics/${t.id}`} className={navLinkClass} title={title}>
                  <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotClass)} aria-hidden="true" />
                  <span className="truncate flex-1">{t.name.charAt(0).toUpperCase() + t.name.slice(1)}</span>
                  {t.field_sentiment !== null && (
                    <span className={cn("shrink-0 text-[11.5px] font-semibold tabular-nums", valClass)}>
                      {Math.round(t.field_sentiment)}
                    </span>
                  )}
                </NavLink>
              );
            })}
            <NavLink to="/app/topics/new" className={navLinkClass}>
              <Plus className="w-4 h-4 shrink-0" />
              New topic
            </NavLink>
          </div>
        </div>

        {/* Configure Section */}
        <div className="mt-8">
          <p className="px-3 mb-2 text-[10px] font-semibold tracking-[0.08em] uppercase text-brand-700">
            Configure
          </p>
          <div className="space-y-0.5">
            {configureNav.map((item) => (
              <NavLink key={item.id} to={item.to} className={navLinkClass}>
                <item.icon className="w-4 h-4 shrink-0" />
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-white/5 space-y-0.5">
        <NavLink to="/app/help" className={navLinkClass}>
          <HelpCircle className="w-4 h-4 shrink-0" />
          Help &amp; docs
        </NavLink>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-[13px] font-medium text-brand-400 hover:text-white hover:bg-white/5 transition-colors"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
